import moment from 'moment';
import { NotificationState } from '../../notification/notification-collection.state';
import { effectiveStatusForEntry, isInProgressCode } from '../../notification/selectors/notification.selector';
import { TimesheetState } from '../timesheet-collection.state';
import { currentEndOfWork } from './end-of-work';

/**
 * Whether the confirmed notifications the current timesheet is carrying have gone stale.
 *
 * They have when the timesheet's end of work fell on an earlier day and nothing was in progress on
 * that day: work already submitted on a day that is over is of no further use, and leaving it in
 * place is what made yesterday's notifications keep showing up in today's timesheet.
 *
 * "In progress on that day" is asked of the notifications rather than the timesheet, because a
 * notification the technician is still working on is exactly what makes the old day's timesheet
 * worth keeping — the entries for it have not been written yet.
 *
 * The rule this states is only half of the decision; whether a user's setup takes part in it at all
 * is the caller's (see `TimesheetEffects.removeStaleNotificationInfo$`).
 *
 * @param timesheet the current timesheet
 * @param entries the notifications the device holds
 * @param now the current instant
 */
export function hasStaleNotificationInfo(
    timesheet: TimesheetState,
    entries: readonly NotificationState[],
    now: Date,
): boolean {
    const endOfWork = currentEndOfWork(timesheet);
    if (endOfWork.isSame(now, 'day')) {
        return false;
    }
    return !entries
        .filter((entry) => isInProgressCode(effectiveStatusForEntry(entry).statusCode))
        .some((entry) => moment(effectiveStatusForEntry(entry).startTime).isSame(endOfWork, 'date'));
}
